import type { Assessment } from "../assessment-types";

export const RELATIONSHIPS_ASSESSMENT: Assessment = {
  slug: "relationships",
  emoji: "💬",
  title: "Relationship Patterns",
  tagline: "Recurring dynamics you may notice",
  intro:
    "This reflects on recurring dynamics that can show up in relationships, from communication to conflict to closeness. It is a starting point for noticing patterns, not a judgement of you or your relationships.",
  questions: [
    { id: "q1", text: "Do you find yourself agreeing with someone even when you actually disagree?" },
    { id: "q2", text: "Do you avoid raising issues because you fear the reaction it might cause?" },
    { id: "q3", text: "Do you notice the same conflicts happening again in different relationships?" },
    { id: "q4", text: "Do you feel responsible for managing other people's moods or emotions?" },
    { id: "q5", text: "Do you struggle to say no, even when you are already stretched thin?" },
    { id: "q6", text: "Do you feel anxious in the lead-up to a difficult conversation?" },
    { id: "q7", text: "Do you find yourself drawn to people who are emotionally unavailable?" },
    { id: "q8", text: "Do you shut down or go quiet during disagreements?" },
    { id: "q9", text: "Do you feel like you lose your own opinions when you are close to someone?" },
    { id: "q10", text: "Do you find it hard to fully relax and be yourself around others?" },
    { id: "q11", text: "Do you notice a pattern of giving far more than you receive?" },
    { id: "q12", text: "Do you feel unsure whether your needs are reasonable to bring up?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your answers suggest fairly steady and workable relationship patterns.",
      body:
        "Communication and conflict do not seem to be dominated by anxiety or avoidance for you right now. That is a good sign for how relationships currently feel.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "You may recognise some recurring relationship patterns.",
      body:
        "There may be moments where speaking up feels hard, or where old dynamics quietly repeat themselves. These patterns often form as ways of keeping relationships peaceful or predictable, especially after earlier difficult experiences.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "You may recognise a recurring pattern in how you relate to others.",
      body:
        "It sounds like certain dynamics, such as over-giving, avoiding conflict or losing your own voice, show up fairly often. These patterns tend to develop for good reason, as ways of staying safe or connected in earlier relationships.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "You may recognise a strong and frequent pattern in your relationships.",
      body:
        "It seems like similar dynamics repeat themselves often and can feel quite entrenched. This is a common response to relationships where being yourself once felt risky, and these patterns can gradually shift with support and practice.",
    },
  ],
  next: [
    { label: "Trauma-informed relationships", to: "/learn/trauma-relationships" },
    { label: "Understanding people-pleasing", to: "/learn/people-pleasing" },
    { label: "Building healthier boundaries", to: "/learn/boundaries" },
    { label: "Try a breathing exercise", to: "/breathing" },
  ],
};
